import {
    Component,
    Element,
    h,
    Host,
    Listen,
    Prop,
    State,
} from '@stencil/core';
import {
    ServiceType,
    ThemeType,
} from '@uipath/portal-shell-util';

import { Scope } from '../../constants/constants';
import { PortalNotificationEvent } from '../../models/appInsights/events/portal-notification.events';
import {
    INotification,
    INotificationReadRequest,
    NotificationPriority,
} from '../../models/notification';
import { ODataQueryOptions } from '../../models/odata';
import {
    PortalActionMode,
    PortalActions,
} from '../../models/portalActions';
import {
    PortalToastType,
    ToastAction,
} from '../../models/toast';
import store from '../../store/store';
import { accountAndTenantsStore } from '../../utils/accountAndTenants/accountAndTenants';
import { isMsiOrStandalone } from '../../utils/EnvironmentUtil';
import { Features } from '../../utils/featureFlags/featureFlagConstants';
import { getFeatureFlag } from '../../utils/featureFlags/featureFlags';
import { getLanguage } from '../../utils/languageHelper';
import { t } from '../../utils/localization/loc';
import Logger from '../../utils/logger/logger';
import { getScopedUrl } from '../../utils/menu';
import { extractErrorMessage } from '../../utils/object.utils';
import { getOrganizationStandardUrl } from '../../utils/orgStandardUrlHelper';
import getSvg, { getTitle } from '../../utils/serviceIconHelper/serviceIconHelper';
import { getAccessToken } from '../../utils/session/sessionStore';
import {
    DeferredNotification,
    deleteNotification,
    fetchNotifications,
    unsubscribeTenantNotification,
    updateLocale,
    updateNotification,
    updateUserNotificationSubcription,
} from './helpers/notificationApi';

let nextUniqueId = 0;

@Component({
    tag: 'portal-notification',
    styleUrl: 'portal-notification.scss',
    shadow: true,
})
export class PortalNotification {
    @Element() $el: HTMLPortalNotificationElement;

    /**
   * Timezone of the UI to show notification, default to browsers timezone or 'Etc/UTC' if not found
   *  @memberof PortalNotification
   */
    @Prop() timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC';

    /**
     * Thinner app-bar
     * @memberof PortalNotification
     * @default true
     * @type {boolean}
     */
    @Prop() thin: boolean = true;

    /**
     * The theme of the notification
     * @memberof PortalNotification
     */
    @Prop() theme: ThemeType = 'autoTheme';

    @State() notifications: INotification[] = [];

    @State() reload = false;

    @State() loading = false;

    @State() unreadNotification = 0;

    @State() showNotificationExpand = false;

    @State() open: boolean;

    private _hasError: boolean;
    private _page = 0;
    private readonly _pageSize = 30;
    private _hasMoreData: boolean = true;
    private _tenants = new Map<string, string>();
    private _isReadOnly = false;
    private _toastDuration = 5000;
    private _toastActionClicked: Set<string> = new Set();

    private get toastElement() {
        return this.$el.shadowRoot!.querySelector('portal-toast')!;
    }

    /**
   * Listening to the outside click to close the dropdown
   * @param ev
   */
    @Listen('keydown', { target: 'document' })
    handleKeydownEvent(ev: KeyboardEvent) {
        if (!this.open) {
            return;
        }
        switch (ev.key) {
            case 'Escape':
            case 'Esc':
                this._closePopup(ev);
                ev.preventDefault();
                break;
            case 'Tab':
                if (ev.shiftKey && this._hasFirstElementFocus()) {
                    this._lastFocusableElement.child.focus();
                    ev.preventDefault();
                } else if (!ev.shiftKey && this._hasLastElementFocus()) {
                    this._firstFocusableElement.child.focus();
                    ev.preventDefault();
                }
                break;
        }
    }

    componentWillLoad() {
        DeferredNotification(getAccessToken());
        updateLocale(getAccessToken(), getLanguage(store));
        this._getCount();
        this._setReceiveNotification();
        accountAndTenantsStore.get('tenants')?.forEach(tenant => this._tenants.set(tenant.id, tenant.name));
    }

    componentDidLoad() {
        store.onChange('locale', () => this.loadNotifications(undefined, true));
    }

    @Listen('loadMoreNotification')
    loadNotifications(_?: CustomEvent, forceRefresh?: boolean) {
        if (!forceRefresh && (!this._hasMoreData || this.loading)) {
            return;
        }
        Logger.logCustomEvent(PortalNotificationEvent.LoadNextBatch, { page: this._page + 1 });
        this.loading = true;
        if (forceRefresh) {
            this.notifications = [];
            this._page = 0;
        }
        fetchNotifications(getAccessToken(), this._getQueryOptions())
            .then(response => {
                this.notifications = [ ...this.notifications, ...response.value ];
                this._hasMoreData = this.notifications.length < response.count;
                this._page++;
                this._hasError = false;
            })
            .catch(__ => {
                this._hasError = true;
            })
            .finally(() => {
                this.loading = false;
                setTimeout(() => (this.showNotificationExpand = true), 100);
            });
    }

    render() {
        return (
            <Host>
                <div class="portal-notification">
                    <div class="portal-notifications-icon">
                        <portal-icon-button
                            name="notifications"
                            expanded={this.open}
                            ariaLabel={t('notifications', getLanguage(store))}
                            class="notification-icon-button-wrapper"
                            cssClass="notification-icon-button"
                            onClick={() => this._openPopup()}
                            theme={this.theme}
                        ></portal-icon-button>
                        {this.unreadNotification > 0 && (
                            <span class="portal-badge">{this.unreadNotification > 99 ? '99+' : this.unreadNotification}</span>
                        )}
                    </div>
                    <div onClick={event => this._closePopup(event)} class="overlay-container">
                        <div class={`notification-container${this.thin ? ' notification-container-thin' : ''}`} role="dialog">
                            <div class="notification-heading">
                                <h1 class="notification-heading-title">
                                    {t('notifications', getLanguage(store))}
                                </h1>
                                {this._isNotificationPreviewVisible() && (
                                    <span class="preview">{t('preview', getLanguage(store))}</span>
                                )}
                                {this._isNotificationPopoutVisible() && (
                                    <div class="notification-header-popout">
                                        <portal-tooltip text={t('notifications-page', getLanguage(store))}>
                                            <portal-icon-button
                                                data-cy="notification-popout-page"
                                                name="launch"
                                                ariaLabel={t('notifications-page', getLanguage(store))}
                                                onClick={(_e) =>
                                                    this._handleNotificationNavigation(_e, 'portal-notifications-page')
                                                }
                                            ></portal-icon-button>
                                        </portal-tooltip>
                                    </div>
                                )}
                            </div>
                            <div class="notification-list apollo-scrollbar">
                                {this.notifications?.filter(x => !x.isHidden).map(notification => {
                                    return (
                                        <div
                                            class={`notification${notification.redirectionUrl ? ' with-redirect-url' : ''}`}
                                            tabindex={0}
                                            onClick={async (event) => await this._redirectToNotificationSource(notification, event)}
                                            onKeyPress={async (e: KeyboardEvent) => {
                                                if ([ 'Enter', ' ' ].includes(e.key)) {
                                                    await this._redirectToNotificationSource(notification);
                                                }
                                            }}
                                        >
                                            <div class="notification-icon">
                                                <portal-tooltip text={getTitle(notification.publisherName?.toLowerCase())}>
                                                    <div class="pt-icon"
                                                        innerHTML={getSvg(notification.publisherName?.toLowerCase())}></div>
                                                </portal-tooltip>
                                                {notification.priority === NotificationPriority.Critical && (
                                                    <portal-icon name="priority_high"></portal-icon>
                                                )}
                                            </div>
                                            <div class="notification-detail">
                                                <div class="notification-info">
                                                    {this._showTenant(notification) && (
                                                        <div class="notification-title">
                                                            {notification.topicName} in {this._tenants.get(notification.tenantId)}
                                                        </div>
                                                    )}
                                                    {!this._showTenant(notification) && (
                                                        <div class="notification-title">{notification.topicName}</div>
                                                    )}
                                                    <div class="notification-published">
                                                        <portal-date
                                                            date={notification.publishedOn}
                                                            relative={true}
                                                            timezone={this.timezone}
                                                        ></portal-date>
                                                    </div>
                                                </div>
                                                <div class={
                                                    'notification-message notification-message-collapsed notification-message-'
                                                    + notification.id
                                                }>
                                                    {notification.message}
                                                </div>
                                                {this._expandable(notification.id) && (
                                                    <button
                                                        title={t('notifications-expandCollapse')}
                                                        aria-label={t('notifications-expandCollapse')}
                                                        onClick={(event: Event) => this._expandMessage(event, notification)}
                                                        onKeyDown={(event: KeyboardEvent) => {
                                                            if ([ 'Enter', ' ' ].includes(event.key)) {
                                                                this._expandMessage(event, notification);
                                                            }
                                                        }}
                                                        class="notification-expand">
                                                        <portal-custom-icon
                                                            name="expand_message"
                                                            size='27px'
                                                        ></portal-custom-icon>
                                                    </button>
                                                )}
                                            </div>
                                            <div class="notification-actions">
                                                <portal-tooltip text={`${notification.isRead ? t('notifications-unread', getLanguage(store)) : t('notifications-read', getLanguage(store))}`}>
                                                    <portal-icon-button
                                                        ariaLabel={`${notification.isRead ? t('notifications-unread', getLanguage(store)) : t('notifications-read', getLanguage(store))}`}
                                                        title={`${notification.isRead ? t('notifications-unread', getLanguage(store)) : t('notifications-read', getLanguage(store))}`}
                                                        size="xs"
                                                        iconSize="8px"
                                                        class={`notification-unread-status ${notification.isRead ? 'notification-read' : 'notification-unread'}`}
                                                        color={`${notification.isRead ? 'inherit' : 'primary'}`}
                                                        name="circle"
                                                        variant="normal"
                                                        onClick={async (event: Event) => {
                                                            event.stopPropagation();
                                                            event.preventDefault();
                                                            await this._markAsReadOrUnread(notification);
                                                        }}
                                                        onKeyDown={async (event: KeyboardEvent) => {
                                                            if ([ 'Enter', ' ' ].includes(event.key)) {
                                                                event.stopPropagation();
                                                                event.preventDefault();
                                                                await this._markAsReadOrUnread(notification);
                                                            }
                                                        }}
                                                    ></portal-icon-button>
                                                </portal-tooltip>
                                                <portal-menu
                                                    class="notification-actions-menu"
                                                    onClick={(event: Event) => event.stopPropagation()}
                                                    onActionClicked={async event => this._handleNotificationAction(event, notification)}
                                                    menu-list={JSON.stringify(this._getMenuList(notification))}
                                                >
                                                    <portal-tooltip text={t('notification-action-menu-text', getLanguage(store))}>
                                                        <portal-icon-button
                                                            aria-label={t('notification-action-menu-text', getLanguage(store))}
                                                            size="sm"
                                                            name="more_vert "></portal-icon-button>
                                                    </portal-tooltip>
                                                </portal-menu>
                                            </div>
                                        </div>
                                    );
                                })}
                                {(!this.notifications || this.notifications.length === 0 || this.loading) && (
                                    <div tabindex={0} class="notifications-empty">
                                        {this.loading && <portal-loader size="24px"></portal-loader>}
                                        {!this.loading && !this._hasError && t('notifications-empty', getLanguage(store))}
                                        {!this.loading && this._hasError && t('notifications-fetch-failure', getLanguage(store))}
                                    </div>
                                )}
                                {this.notifications?.length > 0 && !this.loading && (
                                    <div class="notifications-end-list">{t('notifications-end-list', getLanguage(store))}</div>
                                )}
                                <portal-lazy />
                            </div>
                            <div class="notification-footer">
                                <portal-checkbox
                                    class="portal-notifications-read-unread-checkbox"
                                    uniqueId="portal-notifications-read-unread-checkbox"
                                    defaultChecked={this._isReadOnly}
                                    ariaLabel={t('notifications-unread-only', getLanguage(store))}
                                    onValueChange={(event: CustomEvent<boolean>) => this._handlePortalCheckBoxChange(event.detail)}
                                >
                                    {t('notifications-unread-only', getLanguage(store))}
                                </portal-checkbox>
                                <div class="notification-global-actions">
                                    <portal-tooltip text={t('notifications-settings', getLanguage(store))}>
                                        <portal-icon-button
                                            name="settings"
                                            ariaLabel={t('notifications-settings', getLanguage(store))}
                                            onClick={(_e) => this._handleNotificationNavigation(_e, 'portal-notification-settings')}
                                        ></portal-icon-button>
                                    </portal-tooltip>
                                    <portal-menu
                                        class="notification-global-actions-menu"
                                        onClick={(event: Event) => event.stopPropagation()}
                                        onActionClicked={async event => this._handleNotificationAction(event)}
                                        menu-list={JSON.stringify(this._getMenuList())}
                                    >
                                        <portal-tooltip text={t('notification-allaction-menu-text', getLanguage(store))}>
                                            <portal-icon-button
                                                aria-label={t('notification-allaction-menu-text', getLanguage(store))}
                                                size="sm" name="more_vert"></portal-icon-button>
                                        </portal-tooltip>
                                    </portal-menu>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <portal-toast
                    type={PortalToastType.Success}
                    isopen={false}
                    message=""
                    duration={this._toastDuration}
                    onAction={event => this._handleToastAction(event)}
                />
            </Host >
        );
    }

    private _isNotificationPreviewVisible() {
        return !getFeatureFlag(Features.DisableNotificationPreview.name);
    }

    private _isNotificationPopoutVisible() {
        return getFeatureFlag(Features.EnableNotificationsPopoutPage.name);
    }

    private _handleToastAction(event: CustomEvent<ToastAction>): void {
        this._toastActionClicked.add(event.detail.id);
        this.toastElement.isopen = false;
    }

    private _handlePortalCheckBoxChange(checked: boolean) {
        Logger.logCustomEvent(PortalNotificationEvent.UnreadFilter);
        this._reset();
        this._isReadOnly = checked;
        this.loadNotifications(undefined, true);
    }

    private async _handleNotificationAction(
        event: CustomEvent<{ actionId: string }>,
        notification?: INotification,
    ): Promise<void> {
        try {
            event.stopPropagation();
            switch (event.detail.actionId) {
                case 'portal-notification-topic':
                    if (notification) {
                        const id = 'portal-toast-topic-' + nextUniqueId++;
                        this._showToast(
                            t('notifications-muted', getLanguage(store), { topicOrTenant: notification.topicName }),
                            PortalToastType.Success,
                            [
                                {
                                    id: id,
                                    label: 'Undo',
                                },
                            ],
                        );
                        setTimeout(async () => {
                            if (!this._toastActionClicked.has(id)) {
                                Logger.logCustomEvent(PortalNotificationEvent.UncheckTopicPreference);
                                await updateUserNotificationSubcription(getAccessToken(), {
                                    userSubscriptions: [
                                        {
                                            topicId: notification.topicId,
                                            isSubscribed: false,
                                        },
                                    ],
                                });
                            } else {
                                this._toastActionClicked.delete(id);
                            }
                        }, this._toastDuration);
                    }
                    break;
                case 'portal-notification-tenant':
                    if (notification) {
                        const id = 'portal-toast-tenant-' + nextUniqueId++;
                        this._showToast(
                            t('notifications-muted', getLanguage(store), { topicOrTenant: this._tenants.get(notification.tenantId) }),
                            PortalToastType.Success,
                            [
                                {
                                    id: id,
                                    label: 'Undo',
                                },
                            ],
                        );
                        setTimeout(async () => {
                            if (!this._toastActionClicked.has(id)) {
                                Logger.logCustomEvent(PortalNotificationEvent.UncheckTenantPreference);
                                await unsubscribeTenantNotification(
                                    getAccessToken(),
                                    { tenantId: notification.tenantId },
                                );
                            } else {
                                this._toastActionClicked.delete(id);
                            }
                        }, this._toastDuration);
                    }
                    break;
                case 'portal-notification-all-read':
                    Logger.logCustomEvent(PortalNotificationEvent.ReadAll);
                    await this._markAsReadOrUnread();
                    break;
                case 'portal-notification-clear':
                    if (notification) {
                        const id = 'portal-toast-topic-' + nextUniqueId++;
                        this._showToast(
                            t('notifications-clear-message', getLanguage(store),
                                { topicOrTenant: this._tenants.get(notification.tenantId) }),
                            PortalToastType.Success,
                            [
                                {
                                    id: id,
                                    label: 'Undo',
                                },
                            ],
                        );
                        const notifItem = this.notifications.find(n => n.id === notification.id);
                        if (notifItem !== undefined) {
                            notifItem.isHidden = true;
                            this.reload = !this.reload;
                        }
                        setTimeout(async () => {
                            if (!this._toastActionClicked.has(id)) {
                                Logger.logCustomEvent(PortalNotificationEvent.Clear);
                                await deleteNotification(getAccessToken(), { notifcationIds: [ notification.id ] });
                                this._getCount();
                            } else {
                                const deletednotifItem = this.notifications.find(n => n.id === notification.id);
                                if (deletednotifItem !== undefined) {
                                    deletednotifItem.isHidden = false;
                                }
                                this.reload = !this.reload;
                            }
                        }, this._toastDuration);
                    }
                    break;
                case 'portal-notification-clear-all':
                    Logger.logCustomEvent(PortalNotificationEvent.ClearAll);
                    await deleteNotification(getAccessToken(), {
                        notifcationIds: [],
                        deleteAll: true,
                    });
                    this.notifications = [];
                    this.unreadNotification = 0;
                    break;
            }
        } catch (e) {
            this._showToast(e, PortalToastType.Error);
        }
    }

    private async _handleNotificationNavigation(
        event: Event,
        actionID: string,
    ): Promise<void> {
        event.stopPropagation();
        try {
            switch (actionID) {

                case 'portal-notification-settings':
                    Logger.logCustomEvent(PortalNotificationEvent.ClickPreferences);
                    window
                        .open(
                            `${getScopedUrl(
                                getOrganizationStandardUrl({ shouldIncludeAccountNameOnK8s: true }),
                                '',
                                ServiceType.cloudRpa,
                                Scope.Global,
                                !!isMsiOrStandalone(),
                            )}/notification-settings`,
                            '_blank',
                        )
                        ?.focus();
                    break;
                case 'portal-notifications-page':
                    Logger.logCustomEvent(PortalNotificationEvent.ClickPreferences);
                    window
                        .open(
                            `${getScopedUrl(
                                getOrganizationStandardUrl({ shouldIncludeAccountNameOnK8s: true }),
                                '',
                                ServiceType.cloudRpa,
                                Scope.Global,
                                !!isMsiOrStandalone(),
                            )}/notifications`,
                            '_blank',
                        )
                        ?.focus();
                    break;
            }
        } catch (e) {
            this._showToast(e, PortalToastType.Error);
        }
    }

    private _getMenuList(notification?: INotification): PortalActions[] {
        if (!notification) {
            return [
                {
                    id: 'portal-notification-all-read',
                    name: t('notifications-all-read', getLanguage(store)),
                    type: PortalActionMode.text,
                    disabled: !this.notifications.length,
                },
                {
                    id: 'portal-notification-clear-all',
                    name: t('notifications-clear-all', getLanguage(store)),
                    type: PortalActionMode.text,
                    appearance: 'error',
                    disabled: !this.notifications.length,
                },
            ];
        }
        const list: PortalActions[] = [
            {
                id: 'portal-notification-topic',
                name: t('notifications-mute', getLanguage(store), { topicOrTenant: notification.topicName }),
                type: PortalActionMode.text,
            },
        ];
        if (notification.tenantId) {
            list.push({
                id: 'portal-notification-tenant',
                name: t(
                    'notifications-mute',
                    getLanguage(store),
                    { topicOrTenant: this._tenants.get(notification.tenantId) ?? notification.tenantId },
                ),
                type: PortalActionMode.text,
            });
        }
        list.push({
            id: 'portal-notification-clear',
            name: t('notifications-delete', getLanguage(store)),
            type: PortalActionMode.text,
            appearance: 'error',
        });
        return list;
    }

    private _expandable(id: string): boolean {
        const element = this.$el.shadowRoot?.querySelector('.notification-message-' + id);
        return !!element && element.clientHeight < element.scrollHeight;
    }

    private _expandMessage(event: Event, notification: INotification) {
        event.preventDefault();
        event.stopPropagation();

        const element = this.$el.shadowRoot?.querySelector('.notification-message-' + notification.id);
        if (element?.classList.contains('notification-message-collapsed')) {
            element.classList.remove('notification-message-collapsed');
        } else {
            element?.classList.add('notification-message-collapsed');
        }
    }

    private _showTenant(notification: INotification) {
        return this._tenants.size > 1 && this._tenants.has(notification.tenantId);
    }

    private _getQueryOptions(): ODataQueryOptions | undefined {
        const queryOption: ODataQueryOptions = {
            skip: this._page * this._pageSize,
            top: this._pageSize,
            count: true,
            orderBy: [ 'PublishedOn desc' ],
        };

        if (this._isReadOnly) {
            queryOption.filter = { isRead: { eq: false } };
        }

        return queryOption;
    }

    private _getCount() {
        fetchNotifications(getAccessToken(), {
            count: true,
            top: 1,
            filter: { and: [ { IsRead: { eq: false } } ] },
        })
            .then(response => {
                this.unreadNotification = response.count;
            })
            .catch(_ => {
                this.unreadNotification = 0;
            });
    }

    private get _firstFocusableElement() {
        return {
            parent: this.$el.shadowRoot!,
            child: this.$el.shadowRoot!.querySelector('.notification-heading-title')! as HTMLElement,
        };
    }

    private get _lastFocusableElement() {
        return {
            parent: this.$el.shadowRoot!
                .querySelector('.notification-global-actions-menu > portal-tooltip > portal-icon-button')!.shadowRoot!,
            child: this.$el.shadowRoot!.querySelector('.notification-global-actions-menu > portal-tooltip > portal-icon-button')!
                .shadowRoot!.querySelector('button.portal-icons')! as HTMLElement,
        };
    }

    private _hasFirstElementFocus() {
        return this._firstFocusableElement.parent.activeElement === this._firstFocusableElement.child;
    }

    private _hasLastElementFocus() {
        return this._lastFocusableElement.parent.activeElement === this._lastFocusableElement.child;
    }

    private _openPopup() {
        const overlayContainer = this.$el.shadowRoot?.querySelector('.overlay-container');
        overlayContainer?.classList.toggle('show');
        this.open = true;
        this._firstFocusableElement.child.focus();
        this._getCount();
    }

    private _closePopup(event: Event) {
        if (
            event instanceof MouseEvent &&
            event.target !== this.$el.shadowRoot?.querySelector('.overlay-container.show') &&
            this.open
        ) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const feedContainer = this.$el.shadowRoot?.querySelector('.overlay-container');
        feedContainer?.classList.remove('show');
        this.open = false;
        const iconButton = this.$el.shadowRoot
            ?.querySelector('.notification-icon-button-wrapper')
            ?.shadowRoot?.querySelector('.notification-icon-button') as HTMLElement;
        iconButton?.focus();
        this._reset();
    }

    private async _markAsReadOrUnread(notification?: INotification) {
        try {
            await updateNotification(getAccessToken(), this._constructMarkAsReadRequest(notification));
            this._updateNotificationOnMarkAsRead(notification);
        } catch (e) {
            this._showToast(e, PortalToastType.Error);
        }
    }

    private _constructMarkAsReadRequest(notification?: INotification) {
        const request: INotificationReadRequest = {
            notifications: [],
            forceAllRead: false,
        };
        if (!notification) {
            request.forceAllRead = true;
        } else {
            request.notifications = [
                {
                    notificationId: notification.id,
                    read: !notification.isRead,
                },
            ];
        }

        return request;
    }

    private _updateNotificationOnMarkAsRead(notification?: INotification) {
        if (notification) {
            notification.isRead = !notification.isRead;
            notification.isRead ? this.unreadNotification-- : this.unreadNotification++;
            this.notifications = [ ...this.notifications ];
        } else {
            this.unreadNotification = 0;
            this.notifications = this.notifications.map(n => {
                n.isRead = true;
                return n;
            });
        }
    }

    private _setReceiveNotification(updateConnection?: boolean) {
        if (updateConnection) {
            window.PortalShell.SignalRService?.updateConnection();
        }

        window.PortalShell.SignalRService?.connection.on('ReceiveNotification', (pushedNotification: INotification) => {
            this.unreadNotification++;
            if (this.open) {
                this.notifications = [ pushedNotification, ...this.notifications ];
            }
        });
    }

    private _reset() {
        this._page = 0;
        this.notifications = [];
        this._hasMoreData = true;
        this.showNotificationExpand = false;
    }

    private _showToast(
        message: string | Error,
        type: PortalToastType = PortalToastType.None,
        actions: ToastAction[] = [],
    ) {
        if (typeof message !== 'string') {
            message = extractErrorMessage(message, t('generic-error-message', getLanguage(store)));
            type = PortalToastType.Error;
        }
        this.toastElement.type = type;
        this.toastElement.message = message;
        this.toastElement.actions = actions;
        this.toastElement.isopen = true;
    }

    private async _redirectToNotificationSource(notification: INotification, event?: MouseEvent) {
        if (notification.redirectionUrl) {
            Logger.logCustomEvent(PortalNotificationEvent.Navigate);
            if (!notification.isRead) {
                await this._markAsReadOrUnread(notification);
            }
            if (event?.ctrlKey) {
                window.open(notification.redirectionUrl);
            } else {
                window.open(notification.redirectionUrl, '_self');
            }
        }
    }
}
